import {
  EnvConfig,
  Media,
  MediaType,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  ImageGalleryImage,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AssetFileEditForm } from './assetFileEditForm';
import EditCategory from './editCategory';
import { FilePreviewPanel } from './filesFilePreviewPanel';
import { GalleryPanel } from './filesGalleryPanel';
import { VideoPanel } from './filesVideoPanel';

export const AssetFileEdit = () => {
  const { assetId = '', mediaId = '' } = useParams();
  const navigate = useNavigate();
  const { workspaceAsset } = useWorkspaceAsset(assetId);

  const allFiles: Media[] = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );

  const selectedMedia = useMemo(
    () => allFiles.find((m) => m.id === mediaId) ?? null,
    [allFiles, mediaId]
  );

  const imageFiles = useMemo(
    () => allFiles.filter((m) => m.type === MediaType.Image),
    [allFiles]
  );

  const galleryImages: ImageGalleryImage[] = useMemo(
    () =>
      imageFiles.map((m) => ({
        id: m.id,
        url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
        name: m.name,
      })),
    [imageFiles]
  );

  const galleryInitialIndex = useMemo(() => {
    const idx = galleryImages.findIndex((img) => img.id === mediaId);
    return idx >= 0 ? idx : 0;
  }, [galleryImages, mediaId]);

  type CategoryItem = { id: string; name: string };
  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>([]);

  // Initialize from media when it loads
  useEffect(() => {
    if (selectedMedia?.categories) {
      setSelectedCategories(selectedMedia.categories as CategoryItem[]);
    }
  }, [selectedMedia?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);

  if (!assetId || !selectedMedia) return null;

  const isImage = selectedMedia.type === MediaType.Image;
  const isVideo = selectedMedia.type === MediaType.Video;

  const backToPreview = () =>
    navigate(`/assets/${assetId}/files/${mediaId}`);

  const leftColumn = {
    title:
      selectedMedia.name && selectedMedia.name.length > 30
        ? `${selectedMedia.name.substring(0, 27)}...`
        : selectedMedia.name ?? 'File',
    showBackButton: false,
    disableScroll: true,
    mainComponent: isImage ? (
      <GalleryPanel images={galleryImages} initialIndex={galleryInitialIndex} disableImageChange />
    ) : isVideo ? (
      <VideoPanel media={selectedMedia} />
    ) : (
      <FilePreviewPanel media={selectedMedia} />
    ),
  };

  const rightColumn = {
    title: 'topNav.edit',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: backToPreview,
    disableScroll: true,
    mainComponent: (
      <AssetFileEditForm
        media={selectedMedia}
        assetId={assetId}
        allFileIds={allFiles.map((m) => m.id)}
        onComplete={backToPreview}
        onCategoryEdit={() => setIsCategoryPanelOpen((prev) => !prev)}
        selectedCategories={selectedCategories}
        onCategoriesChange={setSelectedCategories}
      />
    ),
  };

  const thirdColumn = isCategoryPanelOpen
    ? {
        title: 'file.create.category',
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsCategoryPanelOpen(false),
        mainComponent: (
          <EditCategory
            selectedCategories={selectedCategories}
            onCategoriesChange={setSelectedCategories}
          />
        ),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};
